/**
 * Plan 13-02 — CheckerPendingListPage
 *
 * Route: /logsheets/checker/pending
 * Permission gate: logsheet.checker.review
 *
 * Paginated queue of Submitted logsheet entries for the checker's site.
 * Supports multi-select for bulk approval (T-13-14: capped at 100).
 * Mirrors MakerLogsheetsListPage style (Phase 12 Plan 12-03).
 */

import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { BulkApproveDialog } from '@/components/checker/BulkApproveDialog';
import { MonthLockedBadge } from '@/components/monthly/MonthLockedBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCheckerPendingQuery } from '@/hooks/useLogsheetCheckerQueries';
import {
  useActiveMachinesForFilterQuery,
  useActiveSitesForFilterQuery,
} from '@/hooks/useReferenceDataQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatMachineName } from '@/lib/utils/machine-label';
import type { PendingItem, PendingListQuery } from '@/types/logsheet-checker';
import type { LogsheetStatus } from '@/types/logsheets-maker';

/**
 * Phase 26 — status filter options for the queue page. The four logsheet states
 * the user is likely to want to view from this screen; Draft is excluded because
 * checkers don't get visibility into other makers' drafts.
 */
type CheckerQueueStatus = 'Submitted' | 'Approved' | 'Rejected' | 'Checked';

function isCheckerQueueStatus(s: string): s is CheckerQueueStatus {
  return s === 'Submitted' || s === 'Approved' || s === 'Rejected' || s === 'Checked';
}

const PAGE_SIZE = 20;

function parsePositiveInt(s: string | null): number | undefined {
  if (s === null) return undefined;
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function CheckerPendingListPage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFromDate = searchParams.get('fromDate') ?? '';
  const initialToDate = searchParams.get('toDate') ?? '';
  const initialSiteId = parsePositiveInt(searchParams.get('siteId'));
  const initialMachineId = parsePositiveInt(searchParams.get('machineId'));
  const initialStatus: CheckerQueueStatus = (() => {
    const s = searchParams.get('status');
    return s !== null && isCheckerQueueStatus(s) ? s : 'Submitted';
  })();
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();

  const [fromDate, setFromDate] = useState<string>(initialFromDate);
  const [toDate, setToDate] = useState<string>(initialToDate);
  const [siteId, setSiteId] = useState<number | undefined>(initialSiteId);
  const [machineId, setMachineId] = useState<number | undefined>(initialMachineId);
  const [status, setStatus] = useState<CheckerQueueStatus>(initialStatus);
  const [page, setPage] = useState<number>(initialPage);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDialogOpen, setBulkDialogOpen] = useState<boolean>(false);

  // Bulk-approve only makes sense on the default Submitted queue — checking
  // an Approved entry is a no-op.
  const isPendingView = status === 'Submitted';

  const sitesQuery = useActiveSitesForFilterQuery();
  const machinesQuery = useActiveMachinesForFilterQuery();

  useEffect(() => {
    const next = new URLSearchParams();
    if (fromDate !== '') next.set('fromDate', fromDate);
    if (toDate !== '') next.set('toDate', toDate);
    if (siteId !== undefined) next.set('siteId', String(siteId));
    if (machineId !== undefined) next.set('machineId', String(machineId));
    if (status !== 'Submitted') next.set('status', status);
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [fromDate, toDate, siteId, machineId, status, page, setSearchParams]);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [fromDate, toDate, siteId, machineId, status]);

  const listQueryArgs: PendingListQuery = useMemo(() => {
    const out: { -readonly [K in keyof PendingListQuery]: PendingListQuery[K] } = {
      page,
      pageSize: PAGE_SIZE,
    };
    if (fromDate !== '') out.fromDate = fromDate;
    if (toDate !== '') out.toDate = toDate;
    if (siteId !== undefined) out.siteId = siteId;
    if (machineId !== undefined) out.machineId = machineId;
    // Default Submitted is implicit on the backend — only forward the param
    // when the user has flipped to a non-default status.
    if (status !== 'Submitted') out.status = status as LogsheetStatus;
    return out;
  }, [fromDate, toDate, siteId, machineId, status, page]);

  const listQuery = useCheckerPendingQuery(listQueryArgs);

  const columns = useMemo<ColumnDef<PendingItem>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getRowModel().rows.length > 0 &&
              table.getRowModel().rows.every((r) => selectedIds.has(r.original.id))
            }
            onCheckedChange={(checked) => {
              const rows = table.getRowModel().rows;
              if (checked) {
                setSelectedIds((prev) => {
                  const next = new Set(prev);
                  rows.forEach((r) => next.add(r.original.id));
                  return next;
                });
              } else {
                setSelectedIds((prev) => {
                  const next = new Set(prev);
                  rows.forEach((r) => next.delete(r.original.id));
                  return next;
                });
              }
            }}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selectedIds.has(row.original.id)}
            onCheckedChange={(checked) => {
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (checked) {
                  next.add(row.original.id);
                } else {
                  next.delete(row.original.id);
                }
                return next;
              });
            }}
            aria-label={`Select entry ${String(row.original.id)}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          />
        ),
        size: 40,
      },
      {
        accessorKey: 'entryDate',
        header: 'Date',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.entryDate}</span>,
      },
      {
        accessorKey: 'siteName',
        header: 'Site',
        cell: ({ row }) => <span>{row.original.siteName}</span>,
      },
      {
        accessorKey: 'machineName',
        header: 'Machine',
        cell: ({ row }) => (
          <span>
            {formatMachineName(
              row.original.machineName,
              row.original.vehicleNumber,
              row.original.machineCode,
            )}
          </span>
        ),
      },
      {
        accessorKey: 'makerName',
        header: 'Maker',
        cell: ({ row }) => <span>{row.original.makerName}</span>,
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <Badge variant={row.original.type === 'Idle' ? 'secondary' : 'default'}>
            {row.original.type}
          </Badge>
        ),
      },
      {
        accessorKey: 'kpisCount',
        header: 'KPIs',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.kpisCount}</span>,
      },
      {
        accessorKey: 'submittedAt',
        header: 'Submitted At',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {new Date(row.original.submittedAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        accessorKey: 'daysPending',
        header: 'Days Pending',
        cell: ({ row }) => (
          <Badge variant={row.original.daysPending > 2 ? 'destructive' : 'secondary'}>
            {row.original.daysPending}d
          </Badge>
        ),
      },
      // Phase 14 Plan 14-02 (D-128): "From Approver" badge — renders only when
      // routedToCheckerByApprover===true (entry returned from Approver's RouteTo=Checker reject).
      {
        id: 'fromApprover',
        header: 'From Approver',
        cell: ({ row }) =>
          row.original.routedToCheckerByApprover ? (
            <Badge
              variant="outline"
              className="border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-700 dark:bg-orange-950 dark:text-orange-200"
              data-testid={`checker-pending-row-${String(row.original.id)}-from-approver-badge`}
            >
              From Approver
            </Badge>
          ) : null,
      },
      // Phase 15 Plan 15-02: Month lock status badge.
      {
        id: 'monthLocked',
        header: 'Month',
        cell: ({ row }) =>
          row.original.monthLocked === true ? (
            <span data-testid={`checker-pending-row-${String(row.original.id)}-month-locked-badge`}>
              <MonthLockedBadge locked={true} />
            </span>
          ) : null,
      },
    ],
    [selectedIds],
  );

  const data = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const table = useReactTable({
    data: data as PendingItem[],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('logsheet.checker.review')) {
    return <Navigate to="/dashboard" replace />;
  }

  const selectedIdsArray = Array.from(selectedIds);

  return (
    <div className="space-y-6" data-testid="checker-pending-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Checker Queue</h1>
          <p className="text-muted-foreground">
            {status === 'Submitted'
              ? 'Submitted logsheet entries awaiting your review.'
              : status === 'Approved'
                ? 'Approved logsheet entries in your site (read-only).'
                : status === 'Rejected'
                  ? 'Rejected logsheet entries in your site (read-only).'
                  : 'Checked logsheet entries in your site (read-only).'}
          </p>
        </div>
        {isPendingView && selectedIdsArray.length > 0 && (
          <Button
            type="button"
            onClick={() => {
              setBulkDialogOpen(true);
            }}
            data-testid="checker-pending-bulk-approve-trigger"
          >
            Bulk Approve ({selectedIdsArray.length})
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Pending entries</CardTitle>
          <CardDescription>
            Filter by site, machine, or date range. Select entries for bulk approval.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-44 space-y-1">
              <Label htmlFor="checker-filter-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => {
                  if (isCheckerQueueStatus(v)) setStatus(v);
                }}
              >
                <SelectTrigger
                  id="checker-filter-status"
                  data-testid="checker-pending-filter-status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Submitted">Pending (Submitted)</SelectItem>
                  <SelectItem value="Checked">Checked</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="checker-filter-from-date">From date</Label>
              <Input
                id="checker-filter-from-date"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                }}
                data-testid="checker-pending-filter-from-date"
              />
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="checker-filter-to-date">To date</Label>
              <Input
                id="checker-filter-to-date"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                }}
                data-testid="checker-pending-filter-to-date"
              />
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="checker-filter-site">Site</Label>
              <Select
                value={siteId !== undefined ? String(siteId) : 'all'}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setSiteId(undefined);
                  } else {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) setSiteId(n);
                  }
                }}
                disabled={sitesQuery.isLoading}
              >
                <SelectTrigger id="checker-filter-site" data-testid="checker-pending-filter-site">
                  <SelectValue placeholder={sitesQuery.isLoading ? 'Loading…' : 'All sites'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sites</SelectItem>
                  {sitesQuery.data?.items.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-44 space-y-1">
              <Label htmlFor="checker-filter-machine">Machine</Label>
              <Select
                value={machineId !== undefined ? String(machineId) : 'all'}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setMachineId(undefined);
                  } else {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) setMachineId(n);
                  }
                }}
                disabled={machinesQuery.isLoading}
              >
                <SelectTrigger
                  id="checker-filter-machine"
                  data-testid="checker-pending-filter-machine"
                >
                  <SelectValue
                    placeholder={machinesQuery.isLoading ? 'Loading…' : 'All machines'}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All machines</SelectItem>
                  {machinesQuery.data?.items
                    .filter((m) => siteId === undefined || m.siteId === siteId)
                    .map((m) => (
                      <SelectItem key={m.id} value={String(m.id)}>
                        {m.name}{' '}
                        <span className="text-xs text-muted-foreground">({m.code})</span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {listQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="checker-pending-loading">
              Loading pending queue…
            </p>
          )}
          {listQuery.isError && (
            <p className="text-sm text-destructive" data-testid="checker-pending-error">
              Failed to load pending entries.
            </p>
          )}

          {listQuery.data && (
            <>
              <div data-testid="checker-pending-table">
                {/* Mobile card list */}
                <div className="md:hidden space-y-3">
                  {table.getRowModel().rows.length === 0 ? (
                    <p className="text-sm text-center py-8 text-muted-foreground">
                      No pending entries.
                    </p>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => {
                          void navigate(`/logsheets/view/${String(row.original.id)}`);
                        }}
                        className="w-full text-left rounded-lg border p-4 shadow-sm hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <div className="font-medium text-sm">{formatMachineName(row.original.machineName, row.original.vehicleNumber)}</div>
                            <div className="text-xs text-muted-foreground font-mono">{row.original.machineCode}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <Badge variant={row.original.type === 'Idle' ? 'secondary' : 'default'}>{row.original.type}</Badge>
                            <Badge variant={row.original.daysPending > 2 ? 'destructive' : 'secondary'}>{row.original.daysPending}d</Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <div><span className="font-medium text-foreground">Date: </span><span className="font-mono">{row.original.entryDate}</span></div>
                          <div><span className="font-medium text-foreground">Site: </span>{row.original.siteName}</div>
                          <div><span className="font-medium text-foreground">Maker: </span>{row.original.makerName}</div>
                          <div><span className="font-medium text-foreground">KPIs: </span><span className="font-mono">{row.original.kpisCount}</span></div>
                          {row.original.routedToCheckerByApprover && (
                            <div className="col-span-2">
                              <Badge variant="outline" className="border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-700 dark:bg-orange-950 dark:text-orange-200" data-testid={`checker-pending-row-${String(row.original.id)}-from-approver-badge`}>From Approver</Badge>
                            </div>
                          )}
                          {row.original.monthLocked === true && (
                            <div className="col-span-2">
                              <MonthLockedBadge locked={true} />
                            </div>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* Desktop table */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                          {hg.headers.map((h) => (
                            <TableHead key={h.id}>
                              {h.isPlaceholder
                                ? null
                                : flexRender(h.column.columnDef.header, h.getContext())}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {table.getRowModel().rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} className="text-center py-8">
                            <span data-testid="checker-pending-empty">No pending entries.</span>
                          </TableCell>
                        </TableRow>
                      ) : (
                        table.getRowModel().rows.map((row) => (
                          <TableRow
                            key={row.id}
                            data-testid={`checker-pending-row-${String(row.original.id)}`}
                            onClick={() => {
                              void navigate(`/logsheets/view/${String(row.original.id)}`);
                            }}
                            className="cursor-pointer hover:bg-muted/40"
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground">
                  Page {page} of {pageCount} · {total} total
                  {selectedIdsArray.length > 0 && (
                    <span className="ml-2 font-medium text-primary">
                      · {selectedIdsArray.length} selected
                    </span>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={page <= 1}
                    data-testid="checker-pending-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.min(pageCount, p + 1));
                    }}
                    disabled={page >= pageCount}
                    data-testid="checker-pending-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <BulkApproveDialog
        open={bulkDialogOpen}
        entryIds={selectedIdsArray}
        onClose={() => {
          setBulkDialogOpen(false);
        }}
        onSuccess={() => {
          setBulkDialogOpen(false);
          setSelectedIds(new Set());
        }}
      />
    </div>
  );
}
